import { useState } from 'react'
import { BadgeCheck, BadgeX, Search } from 'lucide-react'
import Button from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import TextField from '../../components/TextField'
import { validateCode } from './incidentsService'
import { validationStrings as S } from './strings'
import './incidents.css'

const FIELDS = [
  'identifier',
  'printedBy',
  'printedFor',
  'issueDate',
  'printedName',
  'printedProgram',
  'printedCertDate',
  'language',
]

const asCell = (value) => (typeof value === 'string' ? value.replace('T', ' ') : value)

/**
 * التحقق من صحة الشهادة — Views/CertificateValidation/Index.cshtml.
 *
 * A six-character code resolved against the print history. `outcome` is null before
 * the first check, so the result panel only appears once something has been looked
 * up — the legacy keeps its `#resultContainer` in `d-none` until then.
 */
export default function CertificateValidationPage() {
  const [code, setCode] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState('')
  const [outcome, setOutcome] = useState(null)

  const submit = async (event) => {
    event.preventDefault()
    if (code.trim().length !== 6) {
      setError(S.invalidCode)
      setOutcome(null)
      return
    }
    setError('')
    setChecking(true)
    const found = await validateCode(code)
    setChecking(false)
    setOutcome({ valid: !!found, certificate: found })
  }

  return (
    <>
      <PageHeader title={S.title} subtitle={S.panelTitle} />

      {/*
       * A single-purpose console rather than a form card.
       *
       * This page does ONE thing, and as a left-aligned card holding one short
       * input it read as the corner of a page whose rest had gone missing —
       * six hundred pixels of empty white under a 220px field. Centred, capped,
       * and given the emblem the system screens use, the emptiness around it
       * becomes deliberate: there is nothing else here because there is nothing
       * else to do.
       */}
      <div className="cv">
        <div className="cv__console">
          <span className="cv__emblem" aria-hidden="true">
            <BadgeCheck size={30} strokeWidth={1.5} />
          </span>

          <h2 className="cv__title">
            <bdi>{S.panelTitle}</bdi>
          </h2>
          <p className="cv__heading">
            <bdi>{S.heading}</bdi>
          </p>

          <form className="cv__form" onSubmit={submit} noValidate>
            <TextField
              placeholder={S.placeholder}
              value={code}
              onChange={(value) => {
                setCode(value)
                setError('')
              }}
              maxLength={6}
              dir="ltr"
              invalid={!!error}
              className="cv__code"
            />
            <Button type="submit" disabled={checking}>
              <Search size={16} strokeWidth={1.75} />
              {S.validate}
            </Button>
          </form>

          {error && (
            <p className="cv__error" role="alert">
              <bdi>{error}</bdi>
            </p>
          )}
        </div>

        {outcome && (
          <div className={`cv__result${outcome.valid ? '' : ' cv__result--invalid'}`}>
            <div className="cv__verdict">
              <span className="cv__verdict-icon">
                {outcome.valid ? (
                  <BadgeCheck size={26} strokeWidth={1.75} />
                ) : (
                  <BadgeX size={26} strokeWidth={1.75} />
                )}
              </span>
              <div>
                <h4>
                  <bdi>{outcome.valid ? S.validHead : S.invalidHead}</bdi>
                </h4>
                <p>
                  <bdi>{outcome.valid ? S.validSubHead : S.invalidSubHead}</bdi>
                </p>
              </div>
            </div>

            {outcome.valid && (
              <dl className="cv__fields">
                {FIELDS.map((key) => (
                  <div key={key} className="cv__field">
                    <dt>
                      <bdi>{S.fields[key]}</bdi>
                    </dt>
                    <dd>
                      <bdi>{asCell(outcome.certificate[key]) || '—'}</bdi>
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}
      </div>
    </>
  )
}
